/**
 * The shared placeholder contract (WORKSHOP-FOUNDATION.md §5): {{snake_case}}
 * markers in command templates, filled at use time. Secret-kind values are
 * substituted into the copied text and never persisted anywhere.
 */

export type PlaceholderKind = "text" | "path" | "secret";

export type Placeholder = {
  name: string; // [a-z][a-z0-9_]*, must appear in the template
  label: string;
  hint?: string;
  example?: string;
  kind: PlaceholderKind;
  required: boolean;
};

export const PLACEHOLDER_NAME_RE = /^[a-z][a-z0-9_]*$/;
const MARKER_RE = /\{\{([a-z][a-z0-9_]*)\}\}/g;

/** Names that smell like credentials default to secret kind (never persisted).
 * A cautious default only — authors can flip the kind in the editor. */
const SECRET_NAME_RE = /(^|_)(secret|token|password|passwd|api_?key|credential)s?(_|$)/;

/** Distinct placeholder names referenced by a template, in first-use order. */
export function extractPlaceholderNames(template: string): string[] {
  const names: string[] = [];
  for (const m of template.matchAll(MARKER_RE)) {
    if (!names.includes(m[1])) names.push(m[1]);
  }
  return names;
}

/**
 * Reconcile a card's declared placeholders with its template: keeps declared
 * metadata for names still present, appends bare declarations for new names.
 */
export function syncPlaceholders(template: string, declared: Placeholder[]): Placeholder[] {
  const names = extractPlaceholderNames(template);
  const byName = new Map(declared.map((p) => [p.name, p]));
  return names.map(
    (name) =>
      byName.get(name) ?? {
        name,
        label: name.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
        kind: SECRET_NAME_RE.test(name) ? ("secret" as const) : ("text" as const),
        required: true,
      },
  );
}

/** Fill a template with values; unfilled markers are left intact. */
export function fillTemplate(template: string, values: Record<string, string>): string {
  return template.replace(MARKER_RE, (whole, name: string) =>
    values[name] != null && values[name] !== "" ? values[name] : whole,
  );
}

/** Names still unfilled after applying values (for enabling the Copy button). */
export function missingRequired(
  template: string,
  placeholders: Placeholder[],
  values: Record<string, string>,
): string[] {
  const names = extractPlaceholderNames(template);
  const required = new Set(placeholders.filter((p) => p.required).map((p) => p.name));
  return names.filter((n) => required.has(n) && !(values[n] ?? "").trim());
}

/** Parse the jsonb `placeholders` column defensively. */
export function parsePlaceholders(raw: unknown): Placeholder[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (p): p is Placeholder =>
      !!p &&
      typeof p === "object" &&
      typeof (p as Placeholder).name === "string" &&
      PLACEHOLDER_NAME_RE.test((p as Placeholder).name),
  );
}
